/**
 * onboardingService — thin wrapper for the /welcome aha-moment scan.
 *
 * The backend endpoint is quota-free but still auth-gated. Callers supply a
 * Clerk token (obtained via `useAuth().getToken()`); we just ferry it along.
 */
import apiConfig from '../config/apiConfig';

export interface SponsorBadge {
  name: string;
  logo_url: string | null;
  fit_score: number;            // 0–100
  cpm_estimate_usd: number | null;
}

export interface BrandMatch {
  name: string;
  logo_url: string | null;
  reason: string;
}

export interface RevenueEstimate {
  low: number;
  high: number;
  formula_text: string;
}

export interface FirstScanResponse {
  kind: 'creator' | 'brand' | 'unknown';
  /** True only when we recognize this creator/brand in the DB. False => we have
   *  nothing on file yet and the UI shows a "run a full scan" empty state. */
  is_known?: boolean;
  avatar: string | null;
  handle: string;
  platform: 'youtube' | 'instagram' | 'web' | null;
  followers: number | null;
  niche: string[];
  sponsors: SponsorBadge[];
  revenue_estimate: RevenueEstimate | null;
  brand_matches: BrandMatch[];
}

export const firstScan = async (
  query: string,
  clerkToken: string,
): Promise<FirstScanResponse> => {
  const response = await fetch(
    `${apiConfig.baseUrl}/api/onboarding/first-scan`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${clerkToken}`,
      },
      body: JSON.stringify({ query }),
    },
  );

  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.detail || 'Scan failed. Please try again.');
  }

  return response.json() as Promise<FirstScanResponse>;
};


// ---------------------------------------------------------------------------
// "3 Steps to Value" — new-creator activation progress.
//
// Progress is computed server-side from real rows (a connected channel, a
// brand search, a sent pitch), never from the client — each completed step
// adds a bonus report once, so the server is the only authority on whether a
// step is done and on how long a bonus keeps.
// The endpoint is a POST because it mutates (it grants any earned bonus and
// marks new ones announced as it answers), and it's safe to call on every
// dashboard mount: each bonus is granted once and handed back as new once, so
// the caller must announce whatever comes back, even if it has moved on.
// ---------------------------------------------------------------------------

export type OnboardingStepKey = 'channel' | 'scout' | 'pitch';

export interface OnboardingStep {
  key: OnboardingStepKey;
  title: string;
  blurb: string;
  cta: string;
  href: string;
  /** Bonus reports this step adds (or added, once done). */
  reports: number;
  done: boolean;
}

/** A bonus report grant the creator hasn't been told about yet. */
export interface OnboardingGrant {
  key: OnboardingStepKey;
  reports: number;
  /** Calendar date (YYYY-MM-DD) the bonus stops counting, set by the server. */
  expires_on: string;
}

export interface OnboardingProgress {
  steps: OnboardingStep[];
  all_done: boolean;
  /** Bonus grants not yet announced; this call marks them announced, so each is shown once. */
  just_granted: OnboardingGrant[];
}

export async function getOnboardingProgress(token: string): Promise<OnboardingProgress> {
  const res = await fetch(`${apiConfig.baseUrl}/api/onboarding/progress`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`Onboarding progress failed: ${res.status}`);
  return res.json();
}
